<template>
    <div class="iso-probe-type-toggles">
        <button class="mb-4 flex text-nowrap w-fit items-center justify-center bg-transparent px-4 py-2 text-sm font-medium leading-5 text-gray-700 hover:text-gray-900 hover:no-underline focus:text-gray-900 focus:no-underline"
                @click="goBack"
        >
            <span class="lnr icon-arrow-left !text-sm mr-2"></span> Back
        </button>

        <div v-if="loading" class="text-center py-8">
            <loading :text="'Loading probe types...'"></loading>
        </div>

        <div v-else>
            <div class="grid grid-cols-1 gap-4">
                <div
                    v-for="probeType in probeTypesArray"
                    :key="probeType.id"
                    class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    <!-- Probe Type Information -->
                    <div class="flex-1">
                        <div class="font-semibold text-gray-900">
                            {{ probeType.display_name }}
                        </div>
                        <div class="text-sm text-gray-600 mt-1">
                            <span class="inline-block mr-4">
                                <strong>Type:</strong> {{ probeType.display_name }}
                            </span>
                            <span class="inline-block mr-4">
                                <strong>Part #:</strong> {{ probeType.part_number }}
                            </span>
                            <span class="inline-block">
                                <strong>ID:</strong> {{ probeType.id }}
                            </span>
                        </div>
                    </div>

                    <!-- Is Probe Type Enabled -->
                    <div class="flex items-center ml-4">
                        <span class="text-sm text-gray-600 mr-3">
                            {{ probeTypeEnabled(probeType) ? 'Enabled' : 'Disabled' }}
                        </span>
                        <toggle
                            v-model:value="toggleStates[probeType.id]"
                            :color="'green-600'"
                        ></toggle>
                    </div>
                </div>
            </div>

            <!-- Enable / Disable All -->
            <div    class="mt-4 flex flex-row items-center justify-between">
                <button-blue-secondary      @click="setAllEnabled(true)"
                >
                    Enable All
                </button-blue-secondary>

                <button-blue-secondary      @click="setAllEnabled(false)"
                >
                    Disable All
                </button-blue-secondary>

            </div>
            
            <!-- Save Handling -->
            <div class="mt-4 text-sm text-gray-600 text-center">
                <strong>{{ enabledCount }}</strong> of <strong>{{ totalCount }}</strong> probe types enabled for ISO
            </div>

            <!-- Error Message -->
            <div v-if="errorMessage" class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                {{ errorMessage }}
            </div>

            <!-- Save  -->
            <button-blue-primary    class="mt-4"
                                    @click="saveSettings"
            >
                <span   v-if="! saving">Save</span>
                <span   v-else>Saving</span>
            </button-blue-primary>
        </div>
    </div>
</template>

<script>
import axios from "axios"
import _ from 'lodash'

import Toggle from "@/components/elements/toggle";
import Loading from "@/components/elements/loading";
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from "@/mixins/IconNamesMixin";
import {SidebarLinksPageMixin} from "@/mixins/SidebarLinksPageMixin";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary.vue";
import ButtonBluePrimary from "@/components/elements/buttons/ButtonBluePrimary.vue";

export default {
    name: "IsoProbeTypeToggles",
    mixins: [SonicuCoreMixin, iconNamesMixin, SidebarLinksPageMixin],
    components: {
        ButtonBluePrimary,
        ButtonBlueSecondary,
        Toggle,
        Loading
    },
    props: {
        probeTypes: {
            type: Object,
            required: true
        },
        enabledProbeTypes: {
            type: Array,
            required: true,
            default: () => []
        },
        csrfToken: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            enabledProbeTypesData: [...this.enabledProbeTypes],
            toggleStates: {},
            loading: false,
            saving: false,
            errorMessage: null
        }
    },
    mounted() {
        this.initializeToggleStates();
    },
    methods: {
        probeTypeEnabled(probeType) {
            return _.has(this.toggleStates, probeType.id)
                ? this.toggleStates[probeType.id]
                : false;
        },
        initializeToggleStates() {
            _.each(this.probeTypesArray, probeType => {
                this.toggleStates[probeType.id] = this.enabledProbeTypesData.includes(probeType.id);
            });
        },
        async saveSettings() {
            if (this.saving) {
                return;
            }

            this.saving = true;
            this.errorMessage = null;
            try {
                await axios.post(this.company.buildApiInternalRoute('iso-probe-types'), {
                    enabled_probe_types: this.enabledProbeTypeIds
                });

                // Update the local data with what was saved
                this.enabledProbeTypesData = [...this.enabledProbeTypeIds];

                // Show success message - Laravel flash messages will be shown on page reload
                window.location.reload();
            } catch (error) {
                console.error('Error saving settings:', error);
                this.errorMessage = error.response?.data?.message || 'An error occurred while saving settings. Please try again.';
                this.saving = false;
            }
        },
        setAllEnabled(enabled) {
            _.each(this.probeTypesArray, probeType => {
                this.toggleStates[probeType.id] = enabled;
            });
        },
        goBack() {
            // Navigate back to the production page
            window.location.href = '/production'
        }
    },
    computed: {
        probeTypesArray() {
            return Object.values(this.probeTypes);
        },
        enabledProbeTypeIds() {
            return Object.keys(this.toggleStates)
                .filter(id => this.toggleStates[id])
                .map(id => parseInt(id));
        },
        enabledCount() {
            return this.enabledProbeTypeIds.length;
        },
        totalCount() {
            return this.probeTypesArray.length;
        },
    },
    watch: {

    },
}
</script>

<style scoped>
.iso-probe-type-toggles {
    width: 100%;
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
</style>